import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { SiteEmblem } from "../../components/brand";
import { StepUpChallenge } from "../../components/auth/StepUpChallenge";
import { PageSkeleton } from "../../components/Skeleton";

/**
 * "Confirm it's you — Summit Operations" (Q-O12; ported from Summit Engineering's VerifyIdentityPage): after the Microsoft
 * sign-in, an Admin, the Operations Manager or a Planner enters the code from their authenticator app (setting it up the
 * first time). Shown instead of the app until this sign-in has passed; the API checks it on every request regardless.
 */
export function VerifyIdentityPage() {
  const { principal, loading, refresh, signOut } = useAuth();
  const navigate = useNavigate();
  const returnTo = (useLocation().state as { returnTo?: string } | null)?.returnTo ?? "/";

  if (loading) return <PageSkeleton />;
  if (!principal) return <Navigate to="/sign-in" replace state={{ returnTo }} />;
  if (!principal.needsStepUp) return <Navigate to={returnTo} replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10" data-testid="verify-identity-page">
      <section aria-labelledby="verify-heading" className="w-full max-w-md rounded-xl border border-slate-200 border-t-4 border-t-brand-900 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <SiteEmblem size={40} alt="Summit Operations emblem" className="h-10 w-10 flex-none rounded-full" testId="verify-site-emblem" />
          <div className="leading-tight">
            <h1 id="verify-heading" className="inline-flex items-center gap-2 text-lg font-semibold text-slate-900">
              <ShieldCheck aria-hidden="true" className="h-5 w-5 text-brand-700" />
              Confirm it&apos;s you
            </h1>
            <p className="text-xs text-slate-500">Summit Operations · Summit Aviation, Inc.</p>
          </div>
        </div>
        <p className="mb-4 text-sm text-slate-600">
          Signed in as <strong className="font-medium text-slate-800">{principal.userPrincipalName ?? principal.displayName}</strong>          . Your role (it approves, publishes or
                    administers) needs one more step.
        </p>
        <StepUpChallenge
          onVerified={async () => {
            await refresh();
            navigate(returnTo, { replace: true });
          }}
        />
        <button type="button" className="table-link mt-4 text-xs" onClick={signOut}>
          Not you? Sign out
        </button>
      </section>
    </div>
  );
}
